/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text
*/
<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const { currentAccount } = useAccount();
const { t, locale } = useI18n();
const data = ref({
  questions: [],
  answers: {},
  sessions_remaining: 4,
  applied: false,
});
const text = ref('');
const busy = ref(false);
const url = computed(
  () =>
    '/api/v1/accounts/' + currentAccount.value.id + '/lumerai/knowledge_builder'
);
const questionLabels = {
  company: {
    label: 'Apresente a empresa e o modelo de negocio.',
    hint: 'Nome, segmento, historia curta, regioes atendidas, canais, publico-alvo, diferenciais e objetivo do atendimento.',
  },
  audience: {
    label: 'Quem sao os clientes e quais perfis a IA atende?',
    hint: 'Descreva personas, necessidades, nivel de conhecimento, idioma, horario e como identificar prioridade ou urgencia.',
  },
  offerings: {
    label: 'Quais produtos, servicos e resultados a empresa oferece?',
    hint: 'Detalhe cada oferta, para quem serve, beneficios, requisitos, disponibilidade e o que e proibido prometer.',
  },
  pricing: {
    label:
      'Quais precos, planos, descontos e regras comerciais podem ser informados?',
    hint: 'Inclua valores validos, impostos, parcelamento, validade, condicoes e quando consultar um humano antes de cotar.',
  },
  availability: {
    label: 'Como funciona disponibilidade, prazos e cobertura?',
    hint: 'Informe horarios, SLA, regioes, estoque, prazos, feriados e dados necessarios para uma consulta.',
  },
  locations: {
    label:
      'Quais lojas, filiais, areas de cobertura ou locais devem ser considerados?',
    hint: 'Explique como consultar Locais e Cobertura, campos obrigatorios e o que responder quando nao houver registro.',
  },
  workflow: {
    label: 'Descreva o atendimento ideal passo a passo.',
    hint: 'Saudacao, identificacao da necessidade, perguntas obrigatorias, decisoes, ferramentas, confirmacao e encerramento.',
  },
  data: {
    label: 'Quais dados a IA pode consultar, salvar ou solicitar?',
    hint: 'Liste atributos de contato, documentos, consentimento, validacoes e dados que nunca devem ser armazenados.',
  },
  limits: {
    label: 'O que a IA nunca deve fazer, afirmar ou inventar?',
    hint: 'Defina limites de escopo, promessas, temas sensiveis, politicas, seguranca e como admitir falta de informacao.',
  },
  tone: {
    label: 'Qual tom, idioma e formato de resposta voce espera?',
    hint: 'Formalidade, saudacao, tamanho, emojis, acessibilidade, linguagem inclusiva e como lidar com clientes irritados.',
  },
  handoff: {
    label: 'Quando e para quem a IA deve transferir o atendimento?',
    hint: 'Criterios de transbordo, equipe, horario, SLA, resumo obrigatorio e mensagem exibida ao cliente.',
  },
  integrations: {
    label: 'Quais modulos e integracoes estao ativos e autorizados?',
    hint: 'Liste LumerAI, Workflow, SGP, canais e ferramentas ativas. Diga explicitamente o que pode ser consultado ou executado.',
  },
  faq: {
    label: 'Quais sao as perguntas frequentes e respostas oficiais?',
    hint: 'Inclua perguntas reais, resposta aprovada, links, documentos e variacoes de como o cliente pergunta.',
  },
  examples: {
    label: 'Forneca exemplos de atendimentos bons e casos fora do escopo.',
    hint: 'Mostre dialogos curtos, respostas desejadas, recusas educadas e situacoes que exigem atendimento humano.',
  },
};
const localizedQuestionLabels = computed(() => {
  if (locale.value !== 'en') return questionLabels;

  return Object.fromEntries(
    Object.keys(questionLabels).map(key => [
      key,
      {
        label: t(`LUMERAI_SETTINGS.BUILDER.QUESTIONS.${key}.LABEL`),
        hint: t(`LUMERAI_SETTINGS.BUILDER.QUESTIONS.${key}.HINT`),
      },
    ])
  );
});
const q = computed(() =>
  data.value.questions.find(key => !data.value.answers[key])
);
const currentQuestion = computed(() =>
  q.value
    ? localizedQuestionLabels.value[q.value] || { label: q.value, hint: '' }
    : null
);
const answered = computed(() =>
  data.value.questions.filter(key => data.value.answers[key])
);
async function load() {
  try {
    data.value = (await window.axios.get(url.value)).data;
  } catch (e) {
    useAlert(e.response?.data?.error || 'Falha ao carregar construtor.');
  }
}
async function answer() {
  if (!q.value || !text.value.trim()) {
    useAlert('Escreva uma resposta antes de enviar.');
    return;
  }
  busy.value = true;
  try {
    data.value = (
      await window.axios.post(url.value, { answers: { [q.value]: text.value } })
    ).data;
    text.value = '';
  } catch (e) {
    useAlert(e.response?.data?.error || 'Falha ao salvar resposta.');
  } finally {
    busy.value = false;
  }
}
async function resetBuilder() {
  const warning = data.value.applied
    ? 'A base atualmente aplicada continuara ativa. Deseja iniciar uma nova entrevista?'
    : 'As respostas desta entrevista serao apagadas. Deseja continuar?';
  // Native confirmation is required because the interview responses are deleted.
  // eslint-disable-next-line no-alert
  if (!window.confirm(warning)) return;
  busy.value = true;
  try {
    data.value = (await window.axios.delete(url.value)).data;
    text.value = '';
    useAlert('Construtor reiniciado. A base ativa foi preservada.');
  } catch (e) {
    useAlert(e.response?.data?.error || 'Falha ao reiniciar construtor.');
  } finally {
    busy.value = false;
  }
}
async function apply() {
  busy.value = true;
  try {
    data.value = (await window.axios.patch(url.value, {})).data;
    // Keep the parent settings screen in sync when the builder is completed.
    // The playground sends these values as preview attributes, so stale refs
    // would otherwise hide the newly approved knowledge until a reload.
    window.dispatchEvent(
      new CustomEvent('lumerai-knowledge-updated', {
        detail: {
          ...(data.value.structured || {}),
          ai_knowledge_base: data.value.draft || '',
        },
      })
    );
    useAlert('Base aplicada a LumerAI.');
  } catch (e) {
    useAlert(
      e.response?.data?.error || 'Complete a entrevista antes de aplicar.'
    );
  } finally {
    busy.value = false;
  }
}
onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <section
    class="flex flex-col overflow-hidden rounded-xl border border-n-strong bg-white dark:bg-n-solid-2"
  >
    <header
      class="flex items-center justify-between border-b border-n-strong px-5 py-4"
    >
      <div>
        <h3 class="text-lg font-semibold">
          {{ t('LUMERAI_SETTINGS.BUILDER.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('LUMERAI_SETTINGS.BUILDER.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          :label="t('LUMERAI_SETTINGS.BUILDER.RESET')"
          color="slate"
          sm
          :is-loading="busy"
          @click="resetBuilder"
        />
        <span
          class="rounded-full px-3 py-1 text-xs"
          :class="
            data.applied
              ? 'bg-n-success-1 text-n-success-11'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
        >
          {{
            data.applied
              ? t('LUMERAI_SETTINGS.BUILDER.APPLIED')
              : t('LUMERAI_SETTINGS.BUILDER.USES', {
                  count: data.sessions_remaining,
                })
          }}
        </span>
      </div>
    </header>
    <div class="flex max-h-[560px] flex-col gap-4 overflow-y-auto p-5">
      <div
        class="max-w-[85%] rounded-2xl rounded-tl-sm bg-n-alpha-2 px-4 py-3 text-sm"
      >
        {{ t('LUMERAI_SETTINGS.BUILDER.GREETING') }}
      </div>
      <template v-for="key in answered" :key="key">
        <div
          class="max-w-[85%] rounded-2xl rounded-tl-sm bg-n-alpha-2 px-4 py-3 text-sm"
        >
          <p class="font-medium">
            {{ localizedQuestionLabels[key]?.label || key }}
          </p>
          <p class="mt-2 whitespace-pre-wrap text-n-slate-11">
            {{ data.answers[key] }}
          </p>
        </div>
      </template>
      <div
        v-if="currentQuestion"
        class="max-w-[90%] rounded-2xl rounded-tl-sm bg-n-brand/10 px-4 py-3 text-sm"
      >
        <p class="font-medium">{{ currentQuestion.label }}</p>
        <p class="mt-1 text-n-slate-11">{{ currentQuestion.hint }}</p>
      </div>
      <div
        v-else
        class="max-w-[90%] rounded-2xl rounded-tl-sm bg-n-brand/10 px-4 py-3 text-sm"
      >
        <template v-if="data.applied">
          {{ t('LUMERAI_SETTINGS.BUILDER.APPLY_SUCCESS') }}
          <div v-if="data.structured" class="mt-3 grid gap-2 text-xs">
            <p class="font-semibold">
              {{ t('LUMERAI_SETTINGS.BUILDER.STRUCTURED_FIELDS') }}
            </p>
            <p
              v-for="(value, key) in data.structured"
              :key="key"
              class="rounded bg-n-alpha-2 px-2 py-1"
            >
              <span class="font-medium">{{ key }}:</span>
              <span class="whitespace-pre-wrap">
                {{ Array.isArray(value) ? value.length + ' item(ns)' : value }}
              </span>
            </p>
          </div>
        </template>
        <template v-else>
          {{ t('LUMERAI_SETTINGS.BUILDER.COMPLETE') }}
        </template>
      </div>
    </div>
    <footer class="border-t border-n-strong p-4">
      <div v-if="currentQuestion" class="flex items-end gap-3">
        <TextArea
          v-model="text"
          rows="3"
          class="flex-1"
          :placeholder="t('LUMERAI_SETTINGS.BUILDER.ANSWER_PLACEHOLDER')"
          @keydown.enter.exact.prevent="answer"
        /><Button
          :label="t('LUMERAI_SETTINGS.BUILDER.SEND')"
          color="blue"
          :is-loading="busy"
          @click="answer"
        />
      </div>
      <Button
        v-else-if="!data.applied"
        :label="t('LUMERAI_SETTINGS.BUILDER.APPLY')"
        color="blue"
        :is-loading="busy"
        @click="apply"
      />
    </footer>
  </section>
</template>
